import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "./lib/supabase";
import { scoreWrittenPrompt, generateEmployeeAnalysis, generateTeamAnalytics, rescoreScenario } from "./lib/gemini";
import { generateEmployeePDF } from "./lib/pdf";

/* ═══════════════════════════════════════════════════════════════════
   CONFIG — fallbacks from env, overridden by DB settings at runtime
   ═══════════════════════════════════════════════════════════════════ */
const CFG_DEFAULTS = {
  // #4: default duration = 18 MCQ×1min + 2 scenario×10min = 38min
  duration: parseInt(process.env.REACT_APP_TEST_DURATION || String(18*60 + 2*10*60)),
  passing: parseInt(process.env.REACT_APP_PASSING_SCORE || "70"),
  domain: process.env.REACT_APP_COMPANY_DOMAIN || "bhavnacorp.com",
  company: process.env.REACT_APP_COMPANY_NAME || "Bhavna Corp",
  mcqCount: parseInt(process.env.REACT_APP_MCQ_COUNT || "18"),
  scenarioCount: parseInt(process.env.REACT_APP_SCENARIO_COUNT || "2"),
  authMode: process.env.REACT_APP_AUTH_MODE || "direct",
  title: process.env.REACT_APP_ASSESSMENT_TITLE || "AI Assessment",
  promptMinWords: parseInt(process.env.REACT_APP_PROMPT_MIN_WORDS || "30"),
  promptMaxWords: parseInt(process.env.REACT_APP_PROMPT_MAX_WORDS || "300"),
  aiEnabled: false,
  baseFontSize: 16, // #3: increased base font (+2px from original ~14)
};

// Mutable config — updated from DB on app load
let CFG = { ...CFG_DEFAULTS };

async function loadSettings() {
  try {
    const { data } = await supabase.from("app_settings").select("*");
    if (data) {
      const map = {};
      data.forEach(r => { map[r.key] = r.value; });
      const mc=parseInt(map.mcq_count||CFG_DEFAULTS.mcqCount);
      const sc=parseInt(map.scenario_count||CFG_DEFAULTS.scenarioCount);
      // #4: auto-calc = MCQ×1min + Scenario×10min
      const autoDur=mc*60+sc*10*60;
      CFG = {
        ...CFG_DEFAULTS,
        duration: parseInt(map.test_duration || autoDur),
        passing: parseInt(map.passing_score || CFG_DEFAULTS.passing),
        mcqCount: mc,
        scenarioCount: sc,
        authMode: map.auth_mode || CFG_DEFAULTS.authMode,
        title: map.assessment_title || CFG_DEFAULTS.title,
        promptMinWords: parseInt(map.prompt_min_words || CFG_DEFAULTS.promptMinWords),
        promptMaxWords: parseInt(map.prompt_max_words || CFG_DEFAULTS.promptMaxWords),
        aiEnabled: map.ai_enabled === "true",
        baseFontSize: parseInt(map.base_font_size || CFG_DEFAULTS.baseFontSize), // #3
      };
      // Apply theme
      const themeName=map.theme||"midnight";
      C=getTheme(themeName);
      GCSS=buildCSS(C,CFG.baseFontSize);
    }
  } catch (e) { console.error("Failed to load settings:", e); }
  return CFG;
}

async function saveSetting(key, value, adminEmail) {
  await supabase.from("app_settings").upsert({ key, value: String(value), updated_at: new Date().toISOString(), updated_by: adminEmail }, { onConflict: "key" });
}

/* ═══════════════════════════════════════════════════════════════════
   UTILS
   ═══════════════════════════════════════════════════════════════════ */
function mulberry32(a){return function(){a|=0;a=a+0x6D2B79F5|0;var t=Math.imul(a^a>>>15,1|a);t=t+Math.imul(t^t>>>7,61|t)^t;return((t^t>>>14)>>>0)/4294967296;};}
function genSeed(){return Math.floor(Math.random()*2147483647);}
function genToken(){return Array.from({length:48},()=>"abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random()*36)]).join("");}

function buildTest(mcqPool, scenarioPool, seed) {
  const rng = mulberry32(seed);
  const mcqs = [...mcqPool].sort(() => rng() - 0.5).slice(0, CFG.mcqCount).map(q => {
    const opts = [q.option_a, q.option_b, q.option_c, q.option_d];
    const ix = [0,1,2,3].sort(() => rng() - 0.5);
    return { ...q, type: "mcq", opts: ix.map(i => opts[i]), _correct: ix.indexOf(q.correct_option) };
  });
  const scns = [...scenarioPool].sort(() => rng() - 0.5).slice(0, CFG.scenarioCount).map(s => ({ ...s, type: "scenario" }));
  const all = [...mcqs, ...scns];

  // Separate answer key from questions — answers never in React state
  const answerKey = {};
  const cleanQuestions = all.map((q, i) => {
    if (q.type === "mcq") {
      answerKey[i] = { correct: q._correct, explanation: q.explanation, opts: q.opts };
      // Strip correct answer and explanation from question object
      const { _correct, correct_option, explanation, option_a, option_b, option_c, option_d, ...clean } = q;
      return clean;
    }
    return q;
  });
  return { questions: cleanQuestions, answerKey };
}

/* ═══════════════════════════════════════════════════════════════════
   DESIGN — Premium theme with admin-configurable presets
   ═══════════════════════════════════════════════════════════════════ */

// Theme presets — admin selects from Settings
const THEMES={
  midnight:{bg:"#030712",s1:"#0a1128",s2:"#111d3a",bd:"#1a2d52",ac:"#3b82f6",acD:"#2563eb",gn:"#10b981",rd:"#ef4444",am:"#f59e0b",tx:"#f1f5f9",txS:"#94a3b8",txD:"#5a6a84",grad1:"rgba(59,130,246,.06)",grad2:"rgba(16,185,129,.04)"},
  aurora:{bg:"#050a18",s1:"#0b1530",s2:"#112044",bd:"#1a3060",ac:"#6366f1",acD:"#4f46e5",gn:"#14b8a6",rd:"#f43f5e",am:"#f59e0b",tx:"#f1f5f9",txS:"#a5b4c8",txD:"#6478a0",grad1:"rgba(99,102,241,.06)",grad2:"rgba(20,184,166,.04)"},
  ember:{bg:"#0c0608",s1:"#1a0e12",s2:"#261520",bd:"#3d2030",ac:"#f97316",acD:"#ea580c",gn:"#22c55e",rd:"#ef4444",am:"#eab308",tx:"#fef2f2",txS:"#c4a0a8",txD:"#8a6070",grad1:"rgba(249,115,22,.06)",grad2:"rgba(234,88,12,.04)"},
  ocean:{bg:"#020c14",s1:"#061824",s2:"#0a2438",bd:"#143550",ac:"#06b6d4",acD:"#0891b2",gn:"#10b981",rd:"#f43f5e",am:"#f59e0b",tx:"#ecfeff",txS:"#8bb8c8",txD:"#5a8a9a",grad1:"rgba(6,182,212,.06)",grad2:"rgba(16,185,129,.04)"},
};

function getTheme(name){
  const t=THEMES[name]||THEMES.midnight;
  return{...t,wh:"#fff",acG:t.grad1,gnG:`rgba(16,185,129,.08)`,rdG:`rgba(239,68,68,.08)`,amG:`rgba(245,158,11,.08)`};
}

let C=getTheme("midnight");
const FN={d:"'Sora',system-ui,sans-serif",b:"'DM Sans',system-ui,sans-serif",m:"'JetBrains Mono','Fira Code',monospace"};

function buildCSS(t,baseFontSize=16){
  return`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap');
*{box-sizing:border-box;margin:0}
html{font-size:${baseFontSize}px}
body{background:${t.bg};color:${t.tx};font-family:${FN.b};font-size:${baseFontSize}px}
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
@keyframes glow{0%,100%{box-shadow:0 0 20px ${t.grad1}}50%{box-shadow:0 0 50px ${t.grad1},0 0 80px ${t.grad2}}}
@keyframes orbit{from{transform:rotate(0deg) translateX(120px) rotate(0deg)}to{transform:rotate(360deg) translateX(120px) rotate(-360deg)}}
input:focus,textarea:focus{outline:none;border-color:${t.ac}!important;box-shadow:0 0 0 3px ${t.grad1}!important}
::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:${t.bd};border-radius:3px}
::selection{background:${t.ac};color:#fff}`;
}
let GCSS=buildCSS(C,CFG_DEFAULTS.baseFontSize);

// Animated background with floating orbs
const BgDecor=({theme})=><div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",pointerEvents:"none",overflow:"hidden",zIndex:0}}>
  <div style={{position:"absolute",width:400,height:400,borderRadius:"50%",background:`radial-gradient(circle,${theme.grad1},transparent 70%)`,top:"-10%",left:"-5%",animation:"float 8s ease-in-out infinite"}}/>
  <div style={{position:"absolute",width:300,height:300,borderRadius:"50%",background:`radial-gradient(circle,${theme.grad2},transparent 70%)`,bottom:"-5%",right:"-5%",animation:"float 10s ease-in-out infinite 2s"}}/>
  <div style={{position:"absolute",width:200,height:200,borderRadius:"50%",background:`radial-gradient(circle,${theme.grad1},transparent 70%)`,top:"40%",right:"20%",opacity:.5,animation:"float 12s ease-in-out infinite 4s"}}/>
</div>;

// AIeter Logo SVG — uses theme accent
const Logo=({size=28})=><svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="100" height="100" rx="22" fill="url(#aig)"/>
  <path d="M25 65 A25 25 0 0 1 75 65" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round"/>
  <line x1="50" y1="62" x2="66" y2="38" stroke={C.gn} strokeWidth="3.5" strokeLinecap="round"/>
  <circle cx="50" cy="62" r="4.5" fill="#fff"/>
  <circle cx="25" cy="65" r="3" fill={C.rd} opacity="0.9"/>
  <circle cx="33" cy="45" r="3" fill={C.am} opacity="0.9"/>
  <circle cx="50" cy="37" r="3" fill={C.gn} opacity="0.9"/>
  <circle cx="67" cy="45" r="3" fill={C.gn} opacity="0.9"/>
  <circle cx="75" cy="65" r="3" fill={C.gn} opacity="0.9"/>
  <rect x="22" y="78" width="56" height="5" rx="2.5" fill={C.acD} opacity="0.5"/>
  <rect x="22" y="78" width="40" height="5" rx="2.5" fill={C.gn}/>
  <defs><linearGradient id="aig" x1="0" y1="0" x2="100" y2="100"><stop stopColor={C.ac}/><stop offset="1" stopColor={C.acD}/></linearGradient></defs>
</svg>;

// ── Premium Components ──
const Btn=({children,onClick,v="primary",disabled,full,sz="md",style:sx})=>{
  const vs={
    primary:{bg:`linear-gradient(135deg,${C.ac},${C.acD})`,c:"#fff",sh:`0 4px 24px ${C.grad1},0 1px 3px rgba(0,0,0,.3)`},
    secondary:{bg:`${C.s2}cc`,c:C.tx,bd:`1px solid ${C.bd}`},
    ghost:{bg:"transparent",c:C.txS},
    success:{bg:`linear-gradient(135deg,${C.gn},#059669)`,c:"#fff",sh:`0 4px 20px rgba(16,185,129,.15)`},
    danger:{bg:`linear-gradient(135deg,${C.rd},#dc2626)`,c:"#fff",sh:`0 4px 20px rgba(239,68,68,.15)`}
  };const s=vs[v]||vs.primary;
  return<button onClick={disabled?undefined:onClick} style={{padding:sz==="sm"?"8px 16px":sz==="lg"?"14px 32px":"11px 24px",borderRadius:10,border:s.bd||"none",fontFamily:FN.b,fontSize:sz==="sm"?12:sz==="lg"?15:14,fontWeight:600,cursor:disabled?"not-allowed":"pointer",background:s.bg||"transparent",color:s.c,boxShadow:s.sh||"none",opacity:disabled?.35:1,display:"inline-flex",alignItems:"center",gap:8,justifyContent:"center",width:full?"100%":"auto",transition:"all .25s ease",letterSpacing:".01em",...sx}}>{children}</button>;
};
const Inp=({label,error,...p})=><div style={{marginBottom:14}}>{label&&<label style={{color:C.txS,fontSize:11,fontWeight:600,display:"block",marginBottom:6,letterSpacing:".05em",textTransform:"uppercase"}}>{label}</label>}<input {...p} style={{width:"100%",padding:"12px 16px",borderRadius:10,border:`1px solid ${error?C.rd:C.bd}`,background:`${C.s1}dd`,color:C.tx,fontFamily:p.type==="password"?FN.m:FN.b,fontSize:14,boxSizing:"border-box",outline:"none",transition:"all .2s",...(p.style||{})}}/>{error&&<div style={{color:C.rd,fontSize:11,marginTop:5,fontWeight:500}}>{error}</div>}</div>;
const Card=({children,style:sx,glow})=><div style={{background:`${C.s2}e6`,border:`1px solid ${glow?C.ac+"40":C.bd}`,borderRadius:16,padding:24,boxShadow:glow?`0 0 40px ${C.grad1},0 8px 32px rgba(0,0,0,.2)`:"0 4px 24px rgba(0,0,0,.15)",transition:"all .3s ease",...sx}}>{children}</div>;
const Badge=({children,color=C.ac})=><span style={{background:`${color}18`,color,padding:"4px 12px",borderRadius:20,fontSize:10,fontWeight:700,letterSpacing:".04em",textTransform:"uppercase",whiteSpace:"nowrap",border:`1px solid ${color}20`}}>{children}</span>;
const Bar=({value,max=100,color=C.ac,h=6})=><div style={{background:`${C.s1}80`,borderRadius:h,height:h,overflow:"hidden",width:"100%"}}><div style={{width:`${Math.min((value/max)*100,100)}%`,height:"100%",background:`linear-gradient(90deg,${color},${color}cc)`,borderRadius:h,transition:"width .6s ease",boxShadow:`0 0 8px ${color}30`}}/></div>;
const Hdr=({title,user,onLogout,onBack})=><div style={{background:`${C.s1}e6`,borderBottom:`1px solid ${C.bd}`,padding:"14px 20px",marginBottom:24,position:"relative",zIndex:10}}><div style={{maxWidth:1000,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}><div style={{display:"flex",alignItems:"center",gap:10}}>{onBack&&<Btn v="ghost" onClick={onBack} sz="sm">←</Btn>}<Logo size={30}/><span style={{color:"#fff",fontWeight:700,fontSize:15,fontFamily:FN.d,letterSpacing:"-.02em"}}>{title||CFG.title||"AI Assessment"}</span></div><div style={{display:"flex",alignItems:"center",gap:10}}>{user&&<span style={{color:C.tx,fontSize:12,fontWeight:600}}>{user.name||user.email}</span>}{user?.isAdmin&&<Badge color={C.am}>Admin</Badge>}{onLogout&&<Btn v="ghost" onClick={onLogout} sz="sm">Logout</Btn>}</div></div></div>;
const Wrap=({children,mw=800})=><div style={{maxWidth:mw,margin:"0 auto",padding:"0 20px 50px",position:"relative",zIndex:1}}>{children}</div>;

/* ═══════════════════════════════════════════════════════════════════
   SCREEN: EMAIL ENTRY
   ═══════════════════════════════════════════════════════════════════ */
function EmailScreen({onVerified,onAdmin}){
  const [email,setEmail]=useState("");const [err,setErr]=useState("");const [loading,setLoading]=useState(false);const [sent,setSent]=useState(false);const [token,setToken]=useState("");

  const go=async()=>{
    setErr("");const em=email.toLowerCase().trim();
    if(!em)return setErr("Please enter your email.");
    const emailRegex=/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if(!emailRegex.test(em))return setErr("Please enter a valid email address.");
    setLoading(true);

    // Check if employee exists in pre-imported list
    let{data:emp}=await supabase.from("employees").select("*").eq("email",em).single();
    if(!emp){
      setLoading(false);
      return setErr("You haven't been invited for this assessment. Please contact your admin.");
    }

    // Check attempt history
    const{data:existing}=await supabase.from("test_attempts").select("id,status").eq("employee_id",emp.id).order("started_at",{ascending:false});
    if(existing?.length){
      const latestStatus=existing[0].status;
      // If retake was granted, allow through
      if(latestStatus==="retake_granted"){
        // Good to go — retake approved
      } else if(latestStatus==="submitted"||latestStatus==="scored"){
        setLoading(false);
        return setErr("You have already completed this assessment. Only one attempt is allowed. Contact admin for a retake.");
      } else if(latestStatus==="in_progress"){
        setLoading(false);
        return setErr("You have an assessment in progress. Please contact admin if you need help.");
      }
    }

    // DIRECT MODE: skip magic link, go straight to instructions
    if(CFG.authMode==="direct"){
      setLoading(false);
      onVerified(emp);
      return;
    }

    // MAGIC LINK MODE: generate token
    const{data:existingLink}=await supabase.from("auth_sessions").select("*").eq("employee_id",emp.id).eq("used",false).gt("expires_at",new Date().toISOString()).order("created_at",{ascending:false}).limit(1);

    if(existingLink?.length){
      setToken(existingLink[0].token);setSent(true);setLoading(false);return;
    }

    const tk=genToken();
    const expires=new Date(Date.now()+24*60*60*1000).toISOString();
    await supabase.from("auth_sessions").insert({employee_id:emp.id,token:tk,expires_at:expires});
    setToken(tk);setSent(true);setLoading(false);
  };

  const verifyToken=async()=>{
    const{data:session}=await supabase.from("auth_sessions").select("*,employees(*)").eq("token",token).eq("used",false).single();
    if(!session)return alert("Invalid or expired link.");
    if(new Date(session.expires_at)<new Date())return alert("Link expired.");
    await supabase.from("auth_sessions").update({used:true,used_at:new Date().toISOString()}).eq("id",session.id);
    onVerified(session.employees);
  };

  return(
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20,position:"relative",overflow:"hidden"}}>
      <style>{GCSS}</style>
      <BgDecor theme={C}/>
      <div style={{width:"100%",maxWidth:440,position:"relative",zIndex:1}}>
        <div style={{textAlign:"center",marginBottom:32,animation:"fadeUp .6s ease both"}}>
          <div style={{marginBottom:16,animation:"float 4s ease-in-out infinite"}}><Logo size={60}/></div>
          <h1 style={{color:"#fff",fontSize:26,fontWeight:800,fontFamily:FN.d,margin:"0 0 6px",letterSpacing:"-.03em"}}>{CFG.title}</h1>
          <p style={{color:C.txD,fontSize:13,letterSpacing:".02em"}}>aimeter.in · AI Readiness Program</p>
        </div>
        <Card glow={sent} style={{animation:"fadeUp .6s ease both .1s"}}>
          {!sent?<>
            <p style={{color:C.txS,fontSize:14,marginBottom:20,lineHeight:1.6}}>Enter your company email and start the assessment.</p>
            <Inp label="Email" placeholder="your.email@company.com" type="email" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} onKeyDown={e=>e.key==="Enter"&&go()} error={err}/>
            <Btn onClick={go} full disabled={loading} sz="lg">{loading?"Checking...":CFG.authMode==="direct"?"Start Assessment →":"Send Access Link →"}</Btn>
          </>:<div style={{textAlign:"center"}}>
            <div style={{fontSize:36,marginBottom:8}}>📧</div>
            <h3 style={{color:"#fff",fontSize:16,fontWeight:700,fontFamily:FN.d,margin:"0 0 8px"}}>Access Link Ready!</h3>
            <p style={{color:C.txS,fontSize:13,marginBottom:14}}>In production this emails to <strong style={{color:C.ac}}>{email}</strong>. For demo:</p>
            <div style={{background:C.s1,borderRadius:10,padding:14,marginBottom:14,border:`1px solid ${C.bd}`}}>
              <div style={{color:C.txD,fontSize:10,fontWeight:600,textTransform:"uppercase",marginBottom:6}}>Simulated Magic Link</div>
              <Btn onClick={verifyToken} full sz="lg">🔗 Open Assessment Link</Btn>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}><Badge color={C.txD}>One-time</Badge><Badge color={C.txD}>24h expiry</Badge><Badge color={C.txD}>{CFG.duration/60}m test</Badge></div>
          </div>}
        </Card>
        <div style={{textAlign:"center",marginTop:18}}><span onClick={onAdmin} style={{color:C.txD,fontSize:12,cursor:"pointer",borderBottom:`1px dashed ${C.txD}`}}>Admin Login →</span></div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SCREEN: ADMIN LOGIN
   ═══════════════════════════════════════════════════════════════════ */
function AdminLogin({onLogin,onBack}){
  const [email,setE]=useState("");const [pw,setP]=useState("");const [err,setErr]=useState("");const [loading,setL]=useState(false);
  const go=async()=>{
    setErr("");setL(true);
    const{data,error}=await supabase.rpc("verify_admin_password",{admin_email:email.toLowerCase().trim(),admin_password:pw});
    setL(false);
    if(error||!data?.length)return setErr("Invalid credentials.");
    onLogin({...data[0],isAdmin:true});
  };
  return(<div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20,position:"relative",overflow:"hidden"}}><style>{GCSS}</style><BgDecor theme={C}/><div style={{width:"100%",maxWidth:400,position:"relative",zIndex:1}}>
    <div style={{textAlign:"center",marginBottom:24}}><div style={{marginBottom:10}}><Logo size={44}/></div><h2 style={{color:"#fff",fontSize:19,fontWeight:800,fontFamily:FN.d}}>Admin Access</h2></div>
    <Card><Inp label="Admin Email" placeholder="admin@company.com" value={email} onChange={e=>{setE(e.target.value);setErr("");}}/><Inp label="Password" type="password" placeholder="••••••••" value={pw} onChange={e=>{setP(e.target.value);setErr("");}} onKeyDown={e=>e.key==="Enter"&&go()} error={err}/><Btn onClick={go} full disabled={loading}>{loading?"Verifying...":"Sign In →"}</Btn><div style={{textAlign:"center",marginTop:12}}><Btn v="ghost" onClick={onBack} sz="sm">← Back</Btn></div></Card>
  </div></div>);
}

/* ═══════════════════════════════════════════════════════════════════
   SCREEN: INSTRUCTIONS
   ═══════════════════════════════════════════════════════════════════ */
function Instructions({employee,onStart}){
  const [agreed,setAgreed]=useState(false);
  return(<div style={{minHeight:"100vh",background:C.bg,padding:"30px 20px 50px"}}><style>{GCSS}</style><Wrap mw={660}>
    <div style={{textAlign:"center",marginBottom:24}}><div style={{fontSize:40,marginBottom:6}}>📋</div><h1 style={{color:"#fff",fontSize:21,fontWeight:800,fontFamily:FN.d}}>Assessment Instructions</h1></div>
    <Card style={{marginBottom:18}}>
      <div style={{marginBottom:12}}><Badge color={C.ac}>{employee.email}</Badge></div>
      <div style={{color:C.txS,fontSize:14,lineHeight:1.8}}>
        {[["⏱","Time",`${Math.floor(CFG.duration/60)} minutes total. Auto-submits when time runs out.`],["📝","Questions",`${CFG.mcqCount} MCQs (${CFG.mcqCount} min) + ${CFG.scenarioCount} written prompts (${CFG.scenarioCount*10} min).`],["↩️","Navigation","Use Prev/Next to move between questions. You can return to any question before final submit."],["⏭️","Skip Option","You may skip any question (score = 0) and come back to it later."],["⚠️","Tab Warning","Switching tabs or windows is logged and flagged. Your test will NOT be auto-submitted, but repeated switches will be noted in your report."],["✍️","Written Prompts",`Write prompts from your own knowledge. Min ${CFG.promptMinWords} words, max ${CFG.promptMaxWords} words per response.`],["⏳","After Submit","After submitting, your responses will be evaluated by AI. Please wait on the results page until your score appears — do not close the tab or navigate away."],["🔒","One Attempt","Single attempt per email. No retakes allowed unless granted by admin."]].map(([i,t,d],n)=>(
          <div key={n} style={{display:"flex",gap:11,marginBottom:12,padding:"8px 10px",background:n%2===0?C.s1:"transparent",borderRadius:7}}><span style={{fontSize:18}}>{i}</span><div><strong style={{color:"#fff",fontSize:13}}>{t}:</strong> <span style={{fontSize:13}}>{d}</span></div></div>
        ))}
      </div>
    </Card>
    <Card style={{marginBottom:20,borderColor:`${C.am}30`}}>
      <label style={{display:"flex",gap:11,cursor:"pointer"}} onClick={()=>setAgreed(!agreed)}>
        <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${agreed?C.ac:C.bd}`,background:agreed?C.ac:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>{agreed&&<span style={{color:"#fff",fontSize:11,fontWeight:700}}>✓</span>}</div>
        <span style={{color:C.txS,fontSize:13,lineHeight:1.5}}>I will complete this independently without external help. Tab switches are monitored.</span>
      </label>
    </Card>
    <div style={{textAlign:"center"}}><Btn onClick={onStart} disabled={!agreed} v="success" sz="lg">Start Assessment →</Btn></div>
  </Wrap></div>);
}

/* ═══════════════════════════════════════════════════════════════════
   SCREEN: TEST
   ═══════════════════════════════════════════════════════════════════ */
function TestScreen({employee,questions,answerKey,seed,onComplete}){
  const [cur,setCur]=useState(0);
  const [ans,setAns]=useState({});
  const [skipped,setSkipped]=useState(new Set()); // #5: track skipped questions
  const [time,setTime]=useState(CFG.duration);
  const [done,setDone]=useState(false);
  const [tabs,setTabs]=useState(0);
  const [tabWarnModal,setTabWarnModal]=useState(false); // #1: modal instead of auto-submit
  const [showReview,setShowReview]=useState(false); // #5: review before submit
  const doneRef=useRef(false);const ansRef=useRef({});const tabRef=useRef(0);const t0=useRef(Date.now());
  const skippedRef=useRef(new Set());

  useEffect(()=>{ansRef.current=ans;},[ans]);
  useEffect(()=>{tabRef.current=tabs;},[tabs]);
  useEffect(()=>{skippedRef.current=skipped;},[skipped]);
  useEffect(()=>{if(done)return;const t=setInterval(()=>setTime(p=>{if(p<=1){clearInterval(t);submit();return 0;}return p-1;}),1000);return()=>clearInterval(t);},[done]);

  useEffect(()=>{
    // #1: Tab/window switch — warn popup, log, NO auto-submit
    const onVisChange=()=>{
      if(document.hidden&&!doneRef.current){
        const n=tabRef.current+1;tabRef.current=n;setTabs(n);setTabWarnModal(true);
      }
    };
    const onBlur=()=>{
      if(!doneRef.current){
        const n=tabRef.current+1;tabRef.current=n;setTabs(n);setTabWarnModal(true);
      }
    };
    // #1: keep right-click/devtools blocked; allow Ctrl+C/V for Grammarly compatibility
    const noContext=(e)=>{e.preventDefault();};
    const noKeys=(e)=>{
      if((e.ctrlKey||e.metaKey)&&["p","u"].includes(e.key.toLowerCase())){e.preventDefault();}
      if(e.key.startsWith("F")&&!isNaN(e.key.slice(1))){e.preventDefault();}
      if((e.ctrlKey||e.metaKey)&&e.shiftKey&&["i","j","c"].includes(e.key.toLowerCase())){e.preventDefault();}
      if(e.altKey&&e.key==="Tab"){e.preventDefault();}
    };
    const noDrag=(e)=>{e.preventDefault();};
    // #1: only block selection outside textarea — allows Grammarly on textareas
    const noSelect=(e)=>{if(e.target.tagName!=="TEXTAREA"&&e.target.tagName!=="INPUT")e.preventDefault();};
    const noPrint=(e)=>{if(e.type==="beforeprint"){e.preventDefault();}};
    const onBeforeUnload=(e)=>{if(!doneRef.current){e.preventDefault();e.returnValue="";}};

    document.addEventListener("visibilitychange",onVisChange);
    window.addEventListener("blur",onBlur);
    document.addEventListener("contextmenu",noContext);
    document.addEventListener("keydown",noKeys);
    document.addEventListener("dragstart",noDrag);
    document.addEventListener("selectstart",noSelect);
    window.addEventListener("beforeprint",noPrint);
    window.addEventListener("beforeunload",onBeforeUnload);
    return()=>{
      document.removeEventListener("visibilitychange",onVisChange);
      window.removeEventListener("blur",onBlur);
      document.removeEventListener("contextmenu",noContext);
      document.removeEventListener("keydown",noKeys);
      document.removeEventListener("dragstart",noDrag);
      document.removeEventListener("selectstart",noSelect);
      window.removeEventListener("beforeprint",noPrint);
      window.removeEventListener("beforeunload",onBeforeUnload);
    };
  },[]);

  const submit=useCallback(async()=>{
    if(doneRef.current)return;doneRef.current=true;setDone(true);
    const a=ansRef.current;const mcqs=questions.filter(q=>q.type==="mcq");const scns=questions.filter(q=>q.type==="scenario");
    let mc=0;const mcqD=[];
    mcqs.forEach((q,i)=>{
      const key=answerKey[i]||{};
      const ok=a[i]!==undefined&&!skippedRef.current.has(i)&&a[i]===key.correct;
      if(ok)mc++;
      mcqD.push({question_id:q.id,question_text:q.question||q.q,selected_option:a[i]??null,correct_option:key.correct,is_correct:ok,category:q.category,explanation:key.explanation||"",options:key.opts||q.opts});
    });
    const mcqScore=mcqs.length?Math.round((mc/mcqs.length)*100):0;
    const elapsed=Math.round((Date.now()-t0.current)/1000);

    // Save attempt + MCQ answers immediately
    const{data:attempt}=await supabase.from("test_attempts").insert({
      employee_id:employee.id,seed,mcq_score:mcqScore,mcq_correct:mc,
      mcq_total:mcqs.length,time_used_seconds:Math.min(elapsed,CFG.duration),
      tab_switches:tabRef.current,status:"submitted",
    }).select().single();
    if(!attempt){alert("Error saving. Contact admin.");return;}
    await supabase.from("answers_mcq").insert(mcqD.map(m=>({attempt_id:attempt.id,...m})));

    // Score scenarios — 3 attempts × 8s gap per scenario
    const RETRIES=3;const GAP=8000;
    const scenD=[];
    for(let si=0;si<scns.length;si++){
      const s=scns[si];const idx=mcqs.length+si;const resp=(a[idx]||"").trim();
      const wc=resp.split(/\s+/).filter(Boolean).length;
      let aiResult=null;
      if(CFG.aiEnabled){
        // Only skip if response is truly empty — short responses still get scored (Gemini will score them low)
        if(!resp.trim()){
          console.log(`Scenario ${si+1} empty/skipped — no Gemini call`);
        } else {
          for(let r=1;r<=RETRIES;r++){
            console.log(`Scoring scenario ${si+1}, attempt ${r}/${RETRIES}`);
            const result=await scoreWrittenPrompt(s.scenario,resp);
            if(!result.scoring_pending){
              aiResult=result;
              console.log(`Scenario ${si+1} scored successfully: ${result.score}%`);
              break;
            }
            if(r<RETRIES){
              console.log(`Attempt ${r} failed, waiting ${GAP/1000}s...`);
              await new Promise(res=>setTimeout(res,GAP));
            } else {
              console.warn(`Scenario ${si+1} pending after ${RETRIES} attempts`);
            }
          }
        }
      }
      scenD.push({
        attempt_id:attempt.id,question_id:s.id,scenario_title:s.title,
        scenario_text:s.scenario,category:s.category,response:resp,word_count:wc,
        ai_score:aiResult?.score??null,ai_feedback:aiResult?.feedback??null,
        ai_rubric_hits:aiResult?.rubric_hits||[],raw_ai_response:aiResult?.raw_ai_response||null,
        ai_generated_flag:aiResult?.ai_generated_flag||false,
        ai_generated_reason:aiResult?.ai_generated_reason||"",
      });
      // Gap between scenarios
      if(si<scns.length-1)await new Promise(res=>setTimeout(res,GAP));
    }
    await supabase.from("answers_scenario").insert(scenD);

    // Only count attempted scenarios (non-empty responses) for allScored check
    const attemptedScens=scenD.filter(s=>s.response&&s.response.trim().length>0);
    const scoredScens=scenD.filter(s=>s.ai_score!==null&&s.ai_score!==undefined);
    const allScored=attemptedScens.length===0||scoredScens.length===attemptedScens.length;
    const scenarioScore=CFG.aiEnabled&&scoredScens.length
      ?Math.round(scoredScens.reduce((a,b)=>a+(b.ai_score||0),0)/scoredScens.length)
      :null;
    const totalScore=scenarioScore!==null?Math.round((mcqScore*.5)+(scenarioScore*.5)):null;
    const passed=totalScore!==null?totalScore>=CFG.passing:null;

    await supabase.from("test_attempts").update({
      scenario_score:scenarioScore,total_score:totalScore,passed,
      submitted_at:new Date().toISOString(),status:"scored",
    }).eq("id",attempt.id);

    onComplete({...attempt,mcq_score:mcqScore,scenario_score:scenarioScore,
      total_score:totalScore,passed,ai_analysis:null,
      mcqDetails:mcqD,scenarioDetails:scenD});

    // Fire analysis in background ONLY if all scenarios scored
    // 60s + random jitter (0-30s) to avoid collision with other users
    if(CFG.aiEnabled&&allScored){
      const jitter=Math.floor(Math.random()*30000);
      const delay=60000+jitter;
      console.log(`Analysis scheduled in ${Math.round(delay/1000)}s (all scenarios scored)`);
      setTimeout(async()=>{
        try{
          console.log(`Background: generating employee analysis... [${new Date().toLocaleTimeString()}]`);
          const{data:latestScns}=await supabase.from("answers_scenario").select("*").eq("attempt_id",attempt.id);
          const aiAnalysis=await generateEmployeeAnalysis(
            {...attempt,mcq_score:mcqScore,scenario_score:scenarioScore,total_score:totalScore,passed},
            mcqD,latestScns||scenD
          );
          if(aiAnalysis){
            await supabase.from("test_attempts").update({ai_analysis:aiAnalysis}).eq("id",attempt.id);
            console.log('Background: analysis saved successfully');
          }
        }catch(e){console.error('Background analysis error:',e.message);}
      },delay);
    } else if(CFG.aiEnabled&&!allScored){
      console.warn('Analysis skipped — some scenarios pending. Will trigger after admin rescores.');
    }
  },[questions,answerKey,employee,seed,onComplete]);

  if(done)return<div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}><style>{GCSS}</style><div style={{textAlign:"center",color:C.ac,maxWidth:340,padding:"0 20px"}}>
    <div style={{fontSize:48,marginBottom:16}}>⏳</div>
    <p style={{color:"#fff",fontSize:16,fontWeight:700,fontFamily:FN.d,marginBottom:8}}>Scoring your responses...</p>
    <p style={{color:C.txS,fontSize:13,lineHeight:1.6,marginBottom:16}}>AI is evaluating your written prompts. This takes up to 60 seconds. <strong style={{color:C.am}}>Please keep this tab open.</strong></p>
    <div style={{background:C.s1,borderRadius:10,padding:"10px 16px",border:`1px solid ${C.bd}`}}>
      <p style={{color:C.txD,fontSize:11,margin:0}}>Your MCQ responses are already saved. Only AI scoring is in progress.</p>
    </div>
  </div></div>;

  const q=questions[cur];const mm=String(Math.floor(time/60)).padStart(2,"0");const ss=String(time%60).padStart(2,"0");
  const urgent=time<120;const isMcq=q.type==="mcq";const isLast=cur===questions.length-1;
  const mcqN=questions.filter(x=>x.type==="mcq").length;
  const isSkipped=skipped.has(cur);

  // #5: status helpers for question map
  const qStatus=(i)=>{
    if(skipped.has(i))return"skipped";
    if(questions[i].type==="mcq")return ans[i]!==undefined?"answered":"blank";
    return String(ans[i]||"").split(/\s+/).filter(Boolean).length>=CFG.promptMinWords?"answered":"blank";
  };
  const statusColor=(s)=>s==="answered"?C.gn:s==="skipped"?C.am:C.txD;
  const answeredCount=questions.filter((_,i)=>qStatus(i)==="answered").length;
  const skippedCount=skipped.size;
  const blankCount=questions.length-answeredCount-skippedCount;

  // #5: Review screen
  if(showReview){
    return(<div style={{minHeight:"100vh",background:C.bg,paddingBottom:50}}><style>{GCSS}</style>
      <div style={{background:C.s1,borderBottom:`1px solid ${C.bd}`,padding:"10px 20px",position:"sticky",top:0,zIndex:99,marginBottom:18}}>
        <div style={{maxWidth:800,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}}>
          <span style={{color:"#fff",fontSize:14,fontWeight:700,fontFamily:FN.d}}>📋 Review All Answers</span>
          <div style={{fontFamily:FN.m,fontSize:20,fontWeight:700,color:urgent?C.rd:C.ac,animation:urgent?"pulse .8s infinite":"none"}}>{mm}:{ss}</div>
        </div>
      </div>
      <Wrap>
        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          <Badge color={C.gn}>✓ {answeredCount} answered</Badge>
          {skippedCount>0&&<Badge color={C.am}>⏭ {skippedCount} skipped</Badge>}
          {blankCount>0&&<Badge color={C.rd}>○ {blankCount} blank</Badge>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(52px,1fr))",gap:6,marginBottom:20}}>
          {questions.map((_,i)=>{const st=qStatus(i);const col=statusColor(st);return<button key={i} onClick={()=>{setShowReview(false);setCur(i);}} style={{padding:"8px 4px",borderRadius:8,border:`2px solid ${col}40`,background:`${col}12`,color:col,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:FN.m}}>{i+1}</button>;})}
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
          <Btn v="secondary" onClick={()=>setShowReview(false)}>← Back to Questions</Btn>
          <Btn v="success" sz="lg" onClick={()=>{
            if(blankCount>0||skippedCount>0){if(!window.confirm(`${blankCount} blank + ${skippedCount} skipped = 0 pts each. Submit anyway?`))return;}
            submit();
          }}>Submit Final ✓</Btn>
        </div>
      </Wrap>
    </div>);
  }

  return(<div style={{minHeight:"100vh",background:C.bg,paddingBottom:50}}><style>{GCSS}</style>

    {/* #1: Tab switch warning MODAL — no auto-submit */}
    {tabWarnModal&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.75)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.s2,border:`2px solid ${C.rd}`,borderRadius:16,padding:28,maxWidth:420,width:"100%",textAlign:"center",boxShadow:`0 0 60px ${C.rd}30`}}>
        <div style={{fontSize:36,marginBottom:8}}>⚠️</div>
        <h3 style={{color:C.rd,fontSize:17,fontWeight:800,fontFamily:FN.d,marginBottom:8}}>Tab Switch Detected!</h3>
        <p style={{color:C.txS,fontSize:13,lineHeight:1.6,marginBottom:6}}>You switched away from the test window. This has been logged.</p>
        <p style={{color:C.am,fontSize:13,fontWeight:600,marginBottom:18}}>Total switches this session: <span style={{color:C.rd,fontSize:16}}>{tabs}</span></p>
        <p style={{color:C.txD,fontSize:12,marginBottom:16}}>Please stay on this page. Your test <strong style={{color:C.tx}}>will not</strong> be auto-submitted, but switches are flagged in your report.</p>
        <Btn onClick={()=>setTabWarnModal(false)} v="primary" full>I Understand — Resume Test</Btn>
      </div>
    </div>}

    <div style={{background:C.s1,borderBottom:`1px solid ${C.bd}`,padding:"10px 20px",position:"sticky",top:0,zIndex:99,marginBottom:18}}>
      <div style={{maxWidth:800,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{color:"#fff",fontSize:13,fontWeight:700,fontFamily:FN.d}}>⚡</span>
          <Badge color={C.txD}>{cur+1}/{questions.length}</Badge>
          {cur<mcqN?<Badge color={C.ac}>{q.category}</Badge>:<Badge color={C.am}>{q.category}</Badge>}
          {tabs>0&&<Badge color={C.rd}>⚠ {tabs} switch{tabs>1?"es":""}</Badge>}
        </div>
        <div style={{fontFamily:FN.m,fontSize:20,fontWeight:700,color:urgent?C.rd:C.ac,animation:urgent?"pulse .8s infinite":"none"}}>{mm}:{ss}</div>
      </div>
    </div>

    <Wrap>
      <Bar value={cur+1} max={questions.length} color={cur>=mcqN?C.am:C.ac} h={4}/>

      {/* #5: Question map strip */}
      <div style={{display:"flex",gap:4,flexWrap:"wrap",margin:"10px 0 14px"}}>
        {questions.map((_,i)=>{const st=qStatus(i);const col=statusColor(st);return<button key={i} onClick={()=>setCur(i)} style={{width:28,height:28,borderRadius:6,border:`2px solid ${i===cur?C.ac:col+"50"}`,background:i===cur?`${C.ac}25`:`${col}10`,color:i===cur?C.ac:col,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FN.m,transition:"all .15s"}}>{i+1}</button>;})}
      </div>

      <Card style={{marginTop:4,marginBottom:16}}>
        {!isMcq&&<div style={{marginBottom:10}}><Badge color={C.am}>{q.title}</Badge></div>}
        <h3 style={{color:"#fff",fontSize:15,fontWeight:600,margin:"0 0 16px",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{isMcq?(q.question||q.q):q.scenario}</h3>
        {isSkipped&&<div style={{padding:"10px 14px",borderRadius:9,background:`${C.am}18`,border:`1.5px solid ${C.am}40`,marginBottom:12,color:C.am,fontSize:13,fontWeight:600}}>⏭ Skipped (score = 0). Select an answer or click Unskip to attempt.</div>}
        {isMcq?<div style={{display:"flex",flexDirection:"column",gap:6}}>
          {q.opts.map((opt,oi)=>{const sel=ans[cur]===oi&&!isSkipped;return<button key={oi} onClick={()=>{setAns(p=>({...p,[cur]:oi}));setSkipped(p=>{const n=new Set(p);n.delete(cur);return n;});}} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 13px",borderRadius:10,border:`2px solid ${sel?C.ac:C.bd}`,background:sel?`${C.ac}15`:`${C.s1}dd`,color:sel?"#fff":C.tx,fontSize:14,textAlign:"left",cursor:"pointer",fontFamily:FN.b,transition:"all .15s",lineHeight:1.4,opacity:isSkipped?.5:1}}><span style={{width:22,height:22,borderRadius:6,flexShrink:0,border:`2px solid ${sel?C.ac:C.bd}`,background:sel?C.ac:"transparent",color:sel?"#fff":C.txD,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,fontFamily:FN.m}}>{String.fromCharCode(65+oi)}</span>{opt}</button>;})}
        </div>:<div>
          {(()=>{const wc=String(ans[cur]||"").split(/\s+/).filter(Boolean).length;const tooShort=wc>0&&wc<CFG.promptMinWords;const atMax=wc>=CFG.promptMaxWords;const borderColor=tooShort?C.am:atMax?C.rd:C.bd;return<>
          <textarea value={ans[cur]||""} onChange={e=>{
            const words=e.target.value.split(/\s+/).filter(Boolean).length;
            if(words<=CFG.promptMaxWords){setAns(p=>({...p,[cur]:e.target.value}));setSkipped(p=>{const n=new Set(p);n.delete(cur);return n;});}
          }} placeholder={`Write your complete prompt here...\n\nMinimum ${CFG.promptMinWords} words, maximum ${CFG.promptMaxWords} words.`} rows={10} style={{width:"100%",padding:14,borderRadius:9,border:`1.5px solid ${borderColor}`,background:C.s1,color:C.tx,fontFamily:FN.b,fontSize:14,resize:"vertical",boxSizing:"border-box",lineHeight:1.6,outline:"none",minHeight:200}}/>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
            <span style={{color:tooShort?C.am:atMax?C.rd:C.txD,fontSize:11,fontWeight:tooShort||atMax?600:400}}>
              {wc} / {CFG.promptMaxWords} words {tooShort?`(min ${CFG.promptMinWords})`:atMax?"(limit reached)":""}
            </span>
          </div>
          </>;})()}
        </div>}
      </Card>

      {/* #5: Prev / Skip / Next / Review navigation */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",gap:6}}>
          <Btn v="secondary" onClick={()=>setCur(c=>Math.max(0,c-1))} disabled={cur===0} sz="sm">← Prev</Btn>
          {isSkipped
            ?<Btn v="secondary" sz="sm" onClick={()=>setSkipped(p=>{const n=new Set(p);n.delete(cur);return n;})}>Unskip</Btn>
            :<Btn v="ghost" sz="sm" onClick={()=>{setSkipped(p=>new Set([...p,cur]));if(cur<questions.length-1)setCur(c=>c+1);}}>Skip ⏭</Btn>
          }
        </div>
        <div style={{display:"flex",gap:6}}>
          <Btn v="secondary" sz="sm" onClick={()=>setShowReview(true)}>📋 Review All</Btn>
          {isLast
            ?<Btn v="success" onClick={()=>setShowReview(true)} sz="lg">Review & Submit →</Btn>
            :<Btn onClick={()=>setCur(c=>c+1)}>Next →</Btn>
          }
        </div>
      </div>
    </Wrap>
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════
   SCREEN: EMPLOYEE RESULT
   ═══════════════════════════════════════════════════════════════════ */
function ResultScreen({result}){
  const ai=result.ai_analysis;
  const scoringPending=result.scenario_score===null||result.scenario_score===undefined;
  return(<div style={{minHeight:"100vh",background:C.bg,padding:"30px 20px 50px"}}><style>{GCSS}</style><Wrap mw={700}>
    <Card glow={result.passed} style={{textAlign:"center",marginBottom:22}}>
      <div style={{fontSize:48,marginBottom:6}}>{result.passed?"🏆":scoringPending?"📋":"📘"}</div>
      <h2 style={{color:"#fff",fontSize:21,fontWeight:800,fontFamily:FN.d}}>
        {scoringPending?"Assessment Submitted":result.passed?"Congratulations!":"Assessment Complete"}
      </h2>
      {!scoringPending&&<div style={{display:"inline-flex",alignItems:"baseline",gap:3,color:result.passed?C.gn:C.rd,margin:"10px 0"}}>
        <span style={{fontSize:44,fontWeight:900,fontFamily:FN.m}}>{result.total_score}</span>
        <span style={{fontSize:18,fontWeight:600}}>%</span>
      </div>}
      {!scoringPending&&<div><Badge color={result.passed?C.gn:C.rd}>{result.passed?"PASSED":`BELOW ${CFG.passing}%`}</Badge></div>}
      {scoringPending&&<p style={{color:C.txD,fontSize:13,marginTop:8,lineHeight:1.5}}>Your responses are saved. Written scoring could not complete — your admin will rescore and update your result shortly.</p>}
    </Card>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:20}}>
      <Card style={{textAlign:"center",padding:12}}><div style={{color:C.txD,fontSize:10,fontWeight:600,textTransform:"uppercase"}}>MCQ</div><div style={{color:result.mcq_score>=70?C.gn:C.rd,fontSize:20,fontWeight:800,fontFamily:FN.m}}>{result.mcq_score}%</div></Card>
      <Card style={{textAlign:"center",padding:12}}><div style={{color:C.txD,fontSize:10,fontWeight:600,textTransform:"uppercase"}}>Written</div><div style={{color:scoringPending?C.txD:result.scenario_score>=70?C.gn:C.am,fontSize:20,fontWeight:800,fontFamily:FN.m}}>{scoringPending?"Pending":result.scenario_score+"%"}</div></Card>
      <Card style={{textAlign:"center",padding:12}}><div style={{color:C.txD,fontSize:10,fontWeight:600,textTransform:"uppercase"}}>Time</div><div style={{color:C.ac,fontSize:20,fontWeight:800,fontFamily:FN.m}}>{Math.floor((result.time_used_seconds||0)/60)}m</div></Card>
    </div>
    {ai&&<Card style={{marginBottom:20}}>
      <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:10,fontFamily:FN.d}}>AI Analysis</h3>
      {ai.prompt_readiness_level&&<div style={{marginBottom:8}}><Badge color={ai.prompt_readiness_level==="Advanced"?C.gn:ai.prompt_readiness_level==="Intermediate"?C.am:C.rd}>{ai.prompt_readiness_level}</Badge></div>}
      {ai.overall_assessment&&<p style={{color:C.txS,fontSize:13,lineHeight:1.5,marginBottom:8}}>{ai.overall_assessment}</p>}
      {ai.recommendations?.length>0&&<div style={{marginTop:8}}><p style={{color:C.txD,fontSize:11,fontWeight:600,marginBottom:4}}>RECOMMENDATIONS:</p>{ai.recommendations.map((r,i)=><p key={i} style={{color:C.txS,fontSize:12,marginBottom:4,paddingLeft:8,borderLeft:`2px solid ${C.bd}`}}>{r}</p>)}</div>}
    </Card>}
    {result.scenarioDetails?.map((s,i)=>{
      const pending=s.ai_score===null||s.ai_score===undefined;
      return<Card key={i} style={{marginBottom:10,borderLeft:`3px solid ${pending?C.txD:(s.ai_score||0)>=70?C.gn:(s.ai_score||0)>=40?C.am:C.rd}`}}>
        <div style={{marginBottom:6}}>
          {pending
            ?<Badge color={C.txD}>⏳ {s.scenario_title}: Pending</Badge>
            :<Badge color={(s.ai_score||0)>=70?C.gn:(s.ai_score||0)>=40?C.am:C.rd}>{s.scenario_title}: {s.ai_score}%</Badge>}
        </div>
        {s.ai_feedback&&<p style={{color:C.txS,fontSize:12,margin:"4px 0"}}>{s.ai_feedback}</p>}
        {s.ai_rubric_hits?.length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>{s.ai_rubric_hits.map(h=><Badge key={h} color={C.gn}>{h}</Badge>)}</div>}
      </Card>;
    })}
    <Card style={{marginTop:18,textAlign:"center",background:C.s1}}>
      <p style={{color:C.txS,fontSize:13}}>{scoringPending?"Your responses are saved. Admin will review and update your score.":"Results saved. Your admin will review. You may close this window."}</p>
    </Card>
  </Wrap></div>);
}

/* ═══════════════════════════════════════════════════════════════════
   SCREEN: ADMIN DASHBOARD
   ═══════════════════════════════════════════════════════════════════ */
function AdminDash({admin,onLogout}){
  const [results,setR]=useState([]);const [employees,setE]=useState([]);const [depts,setD]=useState([]);
  const [sel,setSel]=useState(null);const [loading,setL]=useState(true);const [view,setView]=useState("table");
  const [teamAI,setTeamAI]=useState(null);const [aiLoading,setAIL]=useState(false);
  // Admin management
  const [showAddAdmin,setShowAddAdmin]=useState(false);const [newAdmin,setNewAdmin]=useState({email:"",password:"",name:""});
  // #6: Add assessment user
  const [showAddUser,setShowAddUser]=useState(false);const [newUser,setNewUser]=useState({name:"",email:"",department_id:""});const [addUserErr,setAddUserErr]=useState("");
  // Question management
  const [showQ,setShowQ]=useState(false);const [mcqs,setMcqs]=useState([]);const [scenarios,setScenarios]=useState([]);
  // Settings
  const [showSettings,setShowSettings]=useState(false);
  const [settings,setSettings]=useState({auth_mode:"direct",mcq_count:"18",scenario_count:"2",test_duration:"1200",passing_score:"70"});
  // Individual detail
  const [mcqA,setMcqA]=useState([]);const [scnA,setScnA]=useState([]);const [dLoading,setDL]=useState(false);
  // Email import
  const [showImportEmails,setShowImportEmails]=useState(false);
  // Retake search
  const [showRetake,setShowRetake]=useState(false);const [retakeSearch,setRetakeSearch]=useState("");const [retakeReason,setRetakeReason]=useState("");
  // All attempts for selected employee
  const [allAttempts,setAllAttempts]=useState([]);const [selAttemptIdx,setSelAttemptIdx]=useState(0);

  const load=async()=>{
    setL(true);
    const[{data:att},{data:emp},{data:dep},{data:mq},{data:sc},{data:stg}]=await Promise.all([
      supabase.from("test_attempts").select("*,employees(*)").order("started_at",{ascending:false}),
      supabase.from("employees").select("*,team_functions(name)"),
      supabase.from("team_functions").select("*").order("name"),
      supabase.from("questions_mcq").select("*").order("created_at"),
      supabase.from("questions_scenario").select("*").order("created_at"),
      supabase.from("app_settings").select("*"),
    ]);
    setR(att||[]);setE(emp||[]);setD(dep||[]);setMcqs(mq||[]);setScenarios(sc||[]);
    if(stg){const m={};stg.forEach(s=>{m[s.key]=s.value;});setSettings(prev=>({...prev,...m}));}
    setL(false);
  };
  useEffect(()=>{load();},[]);

  const scored=results.filter(r=>r.status==="scored");
  const passed=scored.filter(r=>r.passed).length;
  const avg=scored.length?Math.round(scored.reduce((a,b)=>a+(b.total_score||0),0)/scored.length):0;
  const avgM=scored.length?Math.round(scored.reduce((a,b)=>a+(b.mcq_score||0),0)/scored.length):0;
  const avgW=scored.length?Math.round(scored.reduce((a,b)=>a+(b.scenario_score||0),0)/scored.length):0;
  const flagged=scored.filter(r=>r.tab_switches>0).length;
  const notStarted=employees.filter(e=>!results.find(r=>r.employee_id===e.id)).length;

  const runTeamAI=async()=>{
    setAIL(true);
    const data=scored.map(r=>({department:r.employees?.team_functions?.name||"Unknown",mcq_score:r.mcq_score,scenario_score:r.scenario_score,total_score:r.total_score,passed:r.passed,tab_switches:r.tab_switches}));
    const analysis=await generateTeamAnalytics(data);
    setTeamAI(analysis);setAIL(false);
  };

  const addAdmin=async()=>{
    if(!newAdmin.email||!newAdmin.password||!newAdmin.name)return;
    await supabase.rpc("create_admin",{admin_email:newAdmin.email,admin_password:newAdmin.password,admin_name:newAdmin.name});
    setNewAdmin({email:"",password:"",name:""});setShowAddAdmin(false);alert("Admin added!");
  };

  // #6: Add assessment user
  const addUser=async()=>{
    setAddUserErr("");
    const em=newUser.email.toLowerCase().trim();
    if(!em||!em.includes("@"))return setAddUserErr("Valid email required.");
    const{data:exists}=await supabase.from("employees").select("id").eq("email",em).single();
    if(exists)return setAddUserErr("This email already exists.");
    const ins={email:em,registered_at:new Date().toISOString()};
    if(newUser.name.trim())ins.name=newUser.name.trim();
    if(newUser.department_id.trim())ins.department_id=newUser.department_id.trim();
    const{error}=await supabase.from("employees").insert(ins);
    if(error)return setAddUserErr("Error: "+error.message);
    setNewUser({name:"",email:"",department_id:""});setShowAddUser(false);
    alert(`User ${em} added!`);load();
  };

  // ── MCQ Export/Import ──
  const exportMCQ=()=>{
    const data=mcqs.map(({id,question,option_a,option_b,option_c,option_d,correct_option,category,explanation,is_active})=>({id,question,option_a,option_b,option_c,option_d,correct_option,category,explanation,is_active}));
    const blob=new Blob([JSON.stringify(data,null,2)],{type:"application/json"});
    const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`mcq_questions_${new Date().toISOString().slice(0,10)}.json`;a.click();
  };

  const importMCQ=async(e)=>{
    const file=e.target.files[0];if(!file)return;
    try{
      const data=JSON.parse(await file.text());
      const arr=Array.isArray(data)?data:data.mcq||[];
      if(!arr.length)return alert("No MCQ questions found in file.");
      let count=0;
      for(const q of arr){
        if(!q.question||!q.option_a)continue;
        const{error}=await supabase.from("questions_mcq").insert({question:q.question,option_a:q.option_a,option_b:q.option_b,option_c:q.option_c,option_d:q.option_d,correct_option:q.correct_option??1,category:q.category||"General",explanation:q.explanation||"",is_active:q.is_active!==false});
        if(!error)count++;
      }
      alert(`Imported ${count} MCQ questions.`);load();
    }catch{alert("Invalid JSON file. Check format.");}
    e.target.value="";
  };

  // ── Scenario Export/Import ──
  const exportScenarios=()=>{
    const data=scenarios.map(({id,title,scenario,category,rubric,is_active})=>({id,title,scenario,category,rubric,is_active}));
    const blob=new Blob([JSON.stringify(data,null,2)],{type:"application/json"});
    const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`scenarios_${new Date().toISOString().slice(0,10)}.json`;a.click();
  };

  const importScenarios=async(e)=>{
    const file=e.target.files[0];if(!file)return;
    try{
      const data=JSON.parse(await file.text());
      const arr=Array.isArray(data)?data:data.scenarios||[];
      if(!arr.length)return alert("No scenarios found in file.");
      let count=0;
      for(const s of arr){
        if(!s.title||!s.scenario)continue;
        const{error}=await supabase.from("questions_scenario").insert({title:s.title,scenario:s.scenario,category:s.category||"General",rubric:s.rubric||[],is_active:s.is_active!==false});
        if(!error)count++;
      }
      alert(`Imported ${count} scenarios.`);load();
    }catch{alert("Invalid JSON file. Check format.");}
    e.target.value="";
  };

  const exportCSV=()=>{
    const h=["Email","Name","Dept","Score","MCQ","Written","Passed","Tabs","Date"];
    const rows=scored.map(r=>[r.employees?.email,r.employees?.name||"",r.employees?.team_functions?.name||"",r.total_score+"%",r.mcq_score+"%",r.scenario_score+"%",r.passed?"YES":"NO",r.tab_switches,new Date(r.started_at).toLocaleDateString()]);
    const csv=[h,...rows].map(r=>r.join(",")).join("\n");
    const b=new Blob(["\ufeff"+csv],{type:"text/csv"});const u=URL.createObjectURL(b);const a=document.createElement("a");a.href=u;a.download=`Assessment_${new Date().toISOString().slice(0,10)}.csv`;a.click();
  };

  // ── Email Import ──
  const importEmails=async(e)=>{
    const file=e.target.files[0];if(!file)return;
    const text=await file.text();
    const emails=text.split(/[,\n\r;]+/).map(s=>s.trim().toLowerCase()).filter(s=>s&&s.includes("@"));
    if(!emails.length)return alert("No valid emails found in file.");
    let added=0,skipped=0;
    for(const em of emails){
      const{data:exists}=await supabase.from("employees").select("id").eq("email",em).single();
      if(exists){skipped++;continue;}
      const{error}=await supabase.from("employees").insert({email:em,registered_at:new Date().toISOString()});
      if(!error)added++;else skipped++;
    }
    alert(`Imported ${added} new emails. ${skipped} already existed or invalid.`);
    e.target.value="";load();
  };

  // ── Grant Retake ──
  const MAX_ATTEMPTS=parseInt(settings.max_attempts||"3");
  const grantRetake=async(employeeId,reason)=>{
    if(!reason.trim())return alert("Please provide a reason for the retake.");
    // Count total completed attempts (submitted or scored)
    const allEmpAttempts=results.filter(r=>r.employee_id===employeeId&&(r.status==="submitted"||r.status==="scored"||r.status==="retake_granted"));
    if(allEmpAttempts.length>=MAX_ATTEMPTS){
      return alert(`Maximum ${MAX_ATTEMPTS} attempts reached for this employee. No more retakes allowed.`);
    }
    // Only mark the LATEST scored/submitted attempt as retake_granted
    const latest=results.filter(r=>r.employee_id===employeeId&&(r.status==="submitted"||r.status==="scored")).sort((a,b)=>new Date(b.started_at)-new Date(a.started_at))[0];
    if(!latest)return alert("No completed attempt found for this employee.");
    await supabase.from("test_attempts").update({status:"retake_granted",retake_reason:reason,retake_granted_by:admin.email,retake_granted_at:new Date().toISOString()}).eq("id",latest.id);
    alert("Retake granted! The employee can now take the test again.");
    setRetakeSearch("");setRetakeReason("");setShowRetake(false);load();
  };

  // ── Individual employee detail ──
  useEffect(()=>{
    if(!sel)return;
    setDL(true);
    (async()=>{
      // Load all attempts for this employee
      const{data:attempts}=await supabase.from("test_attempts").select("*").eq("employee_id",sel.employee_id||sel.employees?.id).order("started_at",{ascending:false});
      setAllAttempts(attempts||[]);setSelAttemptIdx(0);
      // Load answers for selected attempt
      const[{data:ma},{data:sa}]=await Promise.all([supabase.from("answers_mcq").select("*").eq("attempt_id",sel.id),supabase.from("answers_scenario").select("*").eq("attempt_id",sel.id)]);
      setMcqA(ma||[]);setScnA(sa||[]);setDL(false);
    })();
  },[sel]);

  // Load answers when switching between attempts
  const [rescoring,setRescoring]=useState(false);

  const runRescore=async()=>{
    if(rescoring)return;
    setRescoring(true);
    const viewAttempt=allAttempts[selAttemptIdx]||sel;
    try{
      const pendingScens=scnA.filter(s=>s.ai_score===null||s.ai_score===undefined);
      if(!pendingScens.length){alert("No pending scenarios to rescore.");setRescoring(false);return;}
      let rescored=0;
      for(const s of pendingScens){
        const result=await rescoreScenario(s.scenario_text,s.response||"");
        if(result&&!result.scoring_pending){
          await supabase.from("answers_scenario").update({
            ai_score:result.score,ai_feedback:result.feedback,
            ai_rubric_hits:result.rubric_hits||[],
            ai_generated_flag:result.ai_generated_flag||false,
            ai_generated_reason:result.ai_generated_reason||"",
            raw_ai_response:result.raw_ai_response||null,
          }).eq("id",s.id);
          rescored++;
        }
      }
      if(rescored>0){
        // Recalculate scores
        const{data:updatedScns}=await supabase.from("answers_scenario").select("*").eq("attempt_id",viewAttempt.id);
        const scored=updatedScns?.filter(s=>s.ai_score!==null&&s.ai_score!==undefined)||[];
        const allScored=scored.length===updatedScns?.length&&scored.length>0;
        const scenarioScore=scored.length?Math.round(scored.reduce((a,b)=>a+(b.ai_score||0),0)/scored.length):null;
        const totalScore=scenarioScore!==null?Math.round((viewAttempt.mcq_score*.5)+(scenarioScore*.5)):null;
        const passed=totalScore!==null?totalScore>=CFG.passing:null;

        await supabase.from("test_attempts").update({
          scenario_score:scenarioScore,total_score:totalScore,passed,status:"scored",
        }).eq("id",viewAttempt.id);

        alert(`Rescored ${rescored} scenario(s) successfully!`);
        await switchAttempt(viewAttempt,selAttemptIdx);
        load();

        // Fire analysis in background only if ALL scenarios now scored
        // 60s + jitter so it doesn't collide with any active scoring sessions
        if(allScored&&CFG.aiEnabled){
          const jitter=Math.floor(Math.random()*30000);
          const delay=60000+jitter;
          console.log(`Analysis scheduled in ${Math.round(delay/1000)}s after rescore`);
          setTimeout(async()=>{
            try{
          console.log(`Background: generating employee analysis after rescore... [${new Date().toLocaleTimeString()}]`);
              const mcqData=mcqA.map(m=>({...m,options:m.options||[]}));
              const aiAnalysis=await generateEmployeeAnalysis(
                {...viewAttempt,mcq_score:viewAttempt.mcq_score,scenario_score:scenarioScore,total_score:totalScore,passed},
                mcqData,updatedScns||[]
              );
              if(aiAnalysis){
                await supabase.from("test_attempts").update({ai_analysis:aiAnalysis}).eq("id",viewAttempt.id);
                console.log('Background: analysis saved after rescore');
              }
            }catch(e){console.error('Background analysis error after rescore:',e.message);}
          },delay);
        } else if(!allScored){
          console.warn('Analysis not triggered — some scenarios still pending after rescore.');
        }
      } else {
        alert("Rescoring failed — API still unavailable. Try again later.");
      }
    }catch(e){console.error("Rescore error:",e);alert("Rescore error: "+e.message);}
    setRescoring(false);
  };

  const switchAttempt=async(att,idx)=>{
    setDL(true);setSelAttemptIdx(idx);
    const[{data:ma},{data:sa}]=await Promise.all([supabase.from("answers_mcq").select("*").eq("attempt_id",att.id),supabase.from("answers_scenario").select("*").eq("attempt_id",att.id)]);
    setMcqA(ma||[]);setScnA(sa||[]);setDL(false);
  };

  if(sel){
    const viewAttempt=allAttempts[selAttemptIdx]||sel;
    const r=viewAttempt;const emp=sel.employees||{};
    const latestScored=allAttempts.find(a=>a.status==="scored");
    const bestAttempt=allAttempts.filter(a=>a.status==="scored").reduce((best,a)=>(!best||a.total_score>best.total_score)?a:best,null);

    const downloadPDF=()=>{generateEmployeePDF(r,{email:emp.email,name:emp.name,department:emp.team_functions?.name},mcqA,scnA,r.ai_analysis);};

    if(dLoading)return<div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}><style>{GCSS}</style><span style={{color:C.ac}}>Loading...</span></div>;

    return(<div style={{minHeight:"100vh",background:C.bg}}><style>{GCSS}</style>
      <Hdr title="Employee Report" user={admin} onLogout={onLogout} onBack={()=>{setSel(null);setAllAttempts([]);}}/>
      <Wrap mw={900}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,flexWrap:"wrap",gap:10}}>
          <div><h2 style={{color:"#fff",fontSize:17,fontWeight:800,fontFamily:FN.d,margin:0}}>{emp.name||emp.email}</h2><span style={{color:C.txD,fontSize:12,fontFamily:FN.m}}>{emp.email}</span></div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Btn v="secondary" onClick={downloadPDF} sz="sm">📥 PDF Report</Btn>
            {scnA.some(s=>s.ai_score===null||s.ai_score===undefined)&&
              <Btn v="primary" onClick={runRescore} disabled={rescoring} sz="sm">{rescoring?"⏳ Rescoring...":"🔄 Rescore Pending"}</Btn>
            }
            <Btn v="danger" onClick={()=>{
              const reason=prompt("Reason for granting retake:");
              if(reason)grantRetake(sel.employee_id||emp.id,reason);
            }} sz="sm">🔄 Grant Retake</Btn>
          </div>
        </div>

        {/* Attempt Switcher */}
        {allAttempts.length>1&&<Card style={{marginBottom:14,padding:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{color:"#fff",fontSize:13,fontWeight:600}}>Attempt History ({allAttempts.length})</span>
            {bestAttempt&&<Badge color={C.ac}>Best: {bestAttempt.total_score}%</Badge>}
            {latestScored&&<Badge color={C.am}>Latest: {latestScored.total_score}% (official)</Badge>}
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {allAttempts.map((att,idx)=>(
              <Btn key={att.id} v={idx===selAttemptIdx?"primary":"secondary"} sz="sm" onClick={()=>switchAttempt(att,idx)}>
                #{allAttempts.length-idx} · {att.total_score!=null?att.total_score+"%":"—"} · {att.status==="retake_granted"?"🔄 Retake Granted":new Date(att.started_at).toLocaleDateString()}
              </Btn>
            ))}
          </div>
          {r.retake_reason&&<div style={{marginTop:8,padding:8,background:C.s1,borderRadius:6}}>
            <span style={{color:C.txD,fontSize:11}}>Retake reason: </span><span style={{color:C.txS,fontSize:11}}>{r.retake_reason}</span>
            {r.retake_granted_by&&<span style={{color:C.txD,fontSize:11}}> — by {r.retake_granted_by}</span>}
          </div>}
        </Card>}

        {/* Score Cards — show current attempt */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:8,marginBottom:20}}>
          {[{l:"Total",v:(r.total_score||0)+"%",c:r.passed?C.gn:C.rd},{l:"MCQ",v:(r.mcq_score||0)+"%",c:(r.mcq_score||0)>=70?C.gn:C.rd},{l:"Written",v:(r.scenario_score||0)+"%",c:(r.scenario_score||0)>=70?C.gn:C.am},{l:"Time",v:`${Math.floor((r.time_used_seconds||0)/60)}m`,c:C.ac},{l:"Tabs",v:r.tab_switches||0,c:r.tab_switches?C.rd:C.gn},{l:"Status",v:r.status||"—",c:r.status==="scored"?C.gn:C.am}].map(s=><Card key={s.l} style={{textAlign:"center",padding:10}}><div style={{color:C.txD,fontSize:9,fontWeight:600,textTransform:"uppercase"}}>{s.l}</div><div style={{color:s.c,fontSize:16,fontWeight:800,fontFamily:FN.m}}>{s.v}</div></Card>)}
        </div>

        {r.ai_analysis&&<Card style={{marginBottom:18}}>
          <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:8,fontFamily:FN.d}}>🤖 AI Analysis</h3>
          {r.ai_analysis.prompt_readiness_level&&<Badge color={r.ai_analysis.prompt_readiness_level==="Advanced"?C.gn:C.am}>{r.ai_analysis.prompt_readiness_level}</Badge>}
          {r.ai_analysis.overall_assessment&&<p style={{color:C.txS,fontSize:13,lineHeight:1.5,margin:"8px 0"}}>{r.ai_analysis.overall_assessment}</p>}
          {r.ai_analysis.recommendations?.map((rec,i)=><p key={i} style={{color:C.txS,fontSize:12,paddingLeft:8,borderLeft:`2px solid ${C.bd}`,marginBottom:4}}>{rec}</p>)}
        </Card>}
        {!r.ai_analysis&&<Card style={{marginBottom:18,borderLeft:`3px solid ${C.txD}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:4,fontFamily:FN.d}}>🤖 AI Analysis</h3>
              <p style={{color:C.txD,fontSize:12,margin:0}}>
                {scnA.some(s=>s.ai_score===null||s.ai_score===undefined)
                  ?"Analysis pending — rescore scenarios first, then analysis will auto-generate."
                  :"Analysis will appear ~60-90s after scoring completes, or trigger manually."}
              </p>
            </div>
            {!scnA.some(s=>s.ai_score===null||s.ai_score===undefined)&&
              <Btn v="secondary" sz="sm" onClick={async()=>{
                const viewAttempt=allAttempts[selAttemptIdx]||sel;
                try{
                  const{data:latestScns}=await supabase.from("answers_scenario").select("*").eq("attempt_id",viewAttempt.id);
                  const scoredScens=latestScns?.filter(s=>s.ai_score!==null)||[];
                  const scenScore=scoredScens.length?Math.round(scoredScens.reduce((a,b)=>a+(b.ai_score||0),0)/scoredScens.length):null;
                  const totScore=scenScore!==null?Math.round((viewAttempt.mcq_score*.5)+(scenScore*.5)):null;
                  const p=totScore!==null?totScore>=CFG.passing:null;
                  const mcqData=mcqA.map(m=>({...m,options:m.options||[]}));
                  const aiAnalysis=await generateEmployeeAnalysis(
                    {...viewAttempt,mcq_score:viewAttempt.mcq_score,scenario_score:scenScore,total_score:totScore,passed:p},
                    mcqData,latestScns||[]
                  );
                  if(aiAnalysis){
                    await supabase.from("test_attempts").update({ai_analysis:aiAnalysis}).eq("id",viewAttempt.id);
                    await switchAttempt(viewAttempt,selAttemptIdx);
                    alert("Analysis generated!");
                  } else {
                    alert("Analysis generation failed. Try again.");
                  }
                }catch(e){alert("Error: "+e.message);}
              }}>✨ Generate Now</Btn>
            }
          </div>
        </Card>}

        <h3 style={{color:"#fff",fontSize:14,fontWeight:700,margin:"0 0 10px",fontFamily:FN.d}}>MCQ ({r.mcq_correct}/{r.mcq_total})</h3>
        {mcqA.map((m,i)=><Card key={i} style={{marginBottom:6,borderLeft:`3px solid ${m.is_correct?C.gn:C.rd}`,padding:12}}>
          <div style={{display:"flex",gap:8,alignItems:"flex-start"}}><span style={{color:m.is_correct?C.gn:C.rd,fontWeight:700,fontSize:12}}>{m.is_correct?"✓":"✗"}</span>
            <div style={{flex:1}}><p style={{color:C.tx,fontSize:12,margin:"0 0 3px",fontWeight:500}}>{m.question_text}</p>
              <span style={{fontSize:11,color:m.is_correct?C.gn:C.rd}}>Ans: {m.options?.[m.selected_option]||"(skipped)"}</span>
              {!m.is_correct&&<span style={{fontSize:11,color:C.gn,marginLeft:10}}>✓ {m.options?.[m.correct_option]}</span>}
            </div><Badge color={C.txD}>{m.category}</Badge></div>
        </Card>)}

        <h3 style={{color:"#fff",fontSize:14,fontWeight:700,margin:"16px 0 10px",fontFamily:FN.d}}>Written</h3>
        {scnA.map((s,i)=><Card key={i} style={{marginBottom:8,borderLeft:`3px solid ${s.ai_score===null||s.ai_score===undefined?C.txD:(s.ai_score||0)>=70?C.gn:(s.ai_score||0)>=40?C.am:C.rd}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6,marginBottom:4}}>
            {s.ai_score===null||s.ai_score===undefined
              ?<Badge color={C.txD}>⏳ {s.scenario_title}: Pending Rescore</Badge>
              :<Badge color={(s.ai_score||0)>=70?C.gn:(s.ai_score||0)>=40?C.am:C.rd}>{s.scenario_title}: {s.ai_score||0}%</Badge>
            }
          </div>
          <p style={{color:C.txD,fontSize:11,margin:"6px 0",fontStyle:"italic"}}>{s.scenario_text?.split("\n")[0]?.slice(0,100)}...</p>
          <div style={{background:C.s1,borderRadius:6,padding:10,margin:"6px 0"}}><p style={{color:C.tx,fontSize:12,margin:0,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{s.response||"No response"}</p></div>
          {s.ai_feedback&&<p style={{color:C.txS,fontSize:12,marginTop:4}}>{s.ai_feedback}</p>}
          {s.ai_rubric_hits?.length>0&&<div style={{display:"flex",gap:3,flexWrap:"wrap",marginTop:4}}>{s.ai_rubric_hits.map(h=><Badge key={h} color={C.gn}>{h}</Badge>)}</div>}
        </Card>)}
      </Wrap>
    </div>);
  }

  if(loading)return<div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}><style>{GCSS}</style><span style={{color:C.ac}}>Loading...</span></div>;

  // ── Main dashboard ──
  return(<div style={{minHeight:"100vh",background:C.bg}}><style>{GCSS}</style>
    <Hdr title="Admin Dashboard" user={admin} onLogout={onLogout}/>
    <Wrap mw={1050}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div><h2 style={{color:"#fff",fontSize:19,fontWeight:800,fontFamily:FN.d,margin:0}}>📊 Assessment Analytics</h2></div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <Btn v="secondary" onClick={load} sz="sm">↻</Btn>
          <Btn v="secondary" onClick={exportCSV} sz="sm">📥 CSV</Btn>
          <label style={{display:"inline-flex",cursor:"pointer"}}><input type="file" accept=".txt,.csv" onChange={importEmails} style={{display:"none"}}/><span style={{padding:"7px 14px",borderRadius:9,border:`1.5px solid ${C.bd}`,background:C.s2,color:C.tx,fontSize:12,fontWeight:600,display:"inline-flex",alignItems:"center",gap:6,cursor:"pointer"}}>📧 Import Emails</span></label>
          <Btn v="secondary" onClick={()=>{setShowAddUser(!showAddUser);setAddUserErr("");}} sz="sm">👤+ Add User</Btn>
          <Btn v="secondary" onClick={()=>setShowRetake(!showRetake)} sz="sm">🔄 Retake</Btn>
          <Btn v="secondary" onClick={()=>setShowQ(!showQ)} sz="sm">📝 Questions</Btn>
          <Btn v="secondary" onClick={()=>setShowSettings(!showSettings)} sz="sm">⚙️ Settings</Btn>
          <Btn v="secondary" onClick={()=>setShowAddAdmin(!showAddAdmin)} sz="sm">👤+ Admin</Btn>
          <Btn onClick={runTeamAI} disabled={aiLoading||!scored.length} sz="sm">{aiLoading?"Analyzing...":"🤖 AI Insights"}</Btn>
        </div>
      </div>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(100px,1fr))",gap:8,marginBottom:20}}>
        {[{l:"Total",v:employees.length,i:"👥",c:C.ac},{l:"Done",v:scored.length,i:"📝",c:C.am},{l:"Pending",v:notStarted,i:"⬜",c:C.txD},{l:"Passed",v:passed,i:"✅",c:C.gn},{l:"Rate",v:scored.length?Math.round(passed/scored.length*100)+"%":"—",i:"📈",c:C.gn},{l:"Avg",v:avg+"%",i:"🎯",c:C.ac},{l:"MCQ",v:avgM+"%",i:"🧠",c:C.ac},{l:"Written",v:avgW+"%",i:"✍️",c:C.am},{l:"Flagged",v:flagged,i:"🚩",c:flagged?C.rd:C.gn}].map(s=><Card key={s.l} style={{textAlign:"center",padding:10}}><div style={{fontSize:16}}>{s.i}</div><div style={{color:s.c,fontSize:16,fontWeight:800,fontFamily:FN.m}}>{s.v}</div><div style={{color:C.txD,fontSize:9,fontWeight:600,textTransform:"uppercase"}}>{s.l}</div></Card>)}
      </div>

      {/* #6: Add User Panel */}
      {showAddUser&&<Card style={{marginBottom:16,borderLeft:`3px solid ${C.gn}`}}>
        <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:12,fontFamily:FN.d}}>👤 Add Assessment User</h3>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:10,alignItems:"end"}}>
          <Inp label="Full Name" placeholder="Jane Smith" value={newUser.name} onChange={e=>setNewUser(p=>({...p,name:e.target.value}))}/>
          <Inp label="Email *" placeholder="jane@company.com" value={newUser.email} onChange={e=>{setNewUser(p=>({...p,email:e.target.value}));setAddUserErr("");}}/>
          <div>
            <label style={{color:C.txS,fontSize:11,fontWeight:600,display:"block",marginBottom:6,letterSpacing:".05em",textTransform:"uppercase"}}>Department</label>
            <select value={newUser.department_id} onChange={e=>setNewUser(p=>({...p,department_id:e.target.value}))} style={{width:"100%",padding:"12px 16px",borderRadius:10,border:`1px solid ${C.bd}`,background:`${C.s1}dd`,color:C.tx,fontFamily:FN.b,fontSize:14,outline:"none"}}>
              <option value="">— Select dept —</option>
              {depts.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <Btn onClick={addUser} sz="sm" v="success" style={{marginBottom:14}}>Add User</Btn>
        </div>
        {addUserErr&&<div style={{color:C.rd,fontSize:12,marginTop:-6,fontWeight:500}}>{addUserErr}</div>}
        <p style={{color:C.txD,fontSize:11,marginTop:6}}>Added users can immediately log in and take the assessment.</p>
      </Card>}

      {/* Retake Search Panel */}
      {showRetake&&<Card style={{marginBottom:16}}>
        <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:10,fontFamily:FN.d}}>🔄 Grant Retake (max {MAX_ATTEMPTS} attempts)</h3>
        <Inp label="Search Employee (email or name)" placeholder="Type to search..." value={retakeSearch} onChange={e=>setRetakeSearch(e.target.value)}/>
        {retakeSearch.length>=2&&<div style={{maxHeight:250,overflowY:"auto",marginBottom:10}}>
          {employees.filter(e=>{const s=retakeSearch.toLowerCase();return(e.email||"").toLowerCase().includes(s)||(e.name||"").toLowerCase().includes(s);}).map(e=>{
            const allEmpAttempts=results.filter(r=>r.employee_id===e.id);
            const completedAttempts=allEmpAttempts.filter(r=>r.status==="submitted"||r.status==="scored"||r.status==="retake_granted");
            const latestStatus=allEmpAttempts.length?allEmpAttempts.sort((a,b)=>new Date(b.started_at)-new Date(a.started_at))[0].status:null;
            const bestScore=completedAttempts.length?Math.max(...completedAttempts.filter(a=>a.total_score!=null).map(a=>a.total_score||0)):null;
            const atMax=completedAttempts.length>=MAX_ATTEMPTS;
            const canRetake=completedAttempts.length>0&&(latestStatus==="submitted"||latestStatus==="scored")&&!atMax;
            const alreadyGranted=latestStatus==="retake_granted";
            return<div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",borderRadius:7,background:C.s1,marginBottom:4}}>
              <div><span style={{color:C.tx,fontSize:13,fontWeight:500}}>{e.name||e.email}</span><span style={{color:C.txD,fontSize:11,marginLeft:8,fontFamily:FN.m}}>{e.email}</span>
                {bestScore!==null&&<span style={{marginLeft:8}}><Badge color={bestScore>=70?C.gn:C.rd}>{bestScore}%</Badge></span>}
                <span style={{marginLeft:8}}><Badge color={C.txD}>{completedAttempts.length}/{MAX_ATTEMPTS} attempts</Badge></span>
                {completedAttempts.length===0&&<span style={{marginLeft:4}}><Badge color={C.txD}>Not attempted</Badge></span>}
                {alreadyGranted&&<span style={{marginLeft:4}}><Badge color={C.am}>Retake pending</Badge></span>}
              </div>
              <div>
                {atMax&&<span style={{color:C.rd,fontSize:11,fontWeight:600}}>Max reached</span>}
                {alreadyGranted&&<span style={{color:C.am,fontSize:11,fontWeight:600}}>Already granted</span>}
                {canRetake&&<Btn v="danger" sz="sm" onClick={()=>{
                  const reason=prompt(`Reason for granting retake to ${e.name||e.email} (attempt ${completedAttempts.length+1}/${MAX_ATTEMPTS}):`);
                  if(reason)grantRetake(e.id,reason);
                }}>Grant Retake</Btn>}
              </div>
            </div>;
          })}
        </div>}
        <p style={{color:C.txD,fontSize:11}}>Search by name or email. Max {MAX_ATTEMPTS} attempts per employee (configurable in Settings).</p>
      </Card>}

      {/* Add Admin Panel */}
      {showAddAdmin&&<Card style={{marginBottom:16}}>
        <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:10,fontFamily:FN.d}}>Add Admin User</h3>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:10,alignItems:"end"}}>
          <Inp label="Name" placeholder="Name" value={newAdmin.name} onChange={e=>setNewAdmin(p=>({...p,name:e.target.value}))}/>
          <Inp label="Email" placeholder="admin@company.com" value={newAdmin.email} onChange={e=>setNewAdmin(p=>({...p,email:e.target.value}))}/>
          <Inp label="Password" type="password" placeholder="Secure password" value={newAdmin.password} onChange={e=>setNewAdmin(p=>({...p,password:e.target.value}))}/>
          <Btn onClick={addAdmin} sz="sm" style={{marginBottom:14}}>Add</Btn>
        </div>
      </Card>}

      {/* Question Management */}
      {showQ&&<Card style={{marginBottom:16}}>
        <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:14,fontFamily:FN.d}}>Question Bank</h3>

        {/* MCQ Section */}
        <div style={{background:C.s1,borderRadius:9,padding:14,marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div><span style={{color:"#fff",fontSize:13,fontWeight:600}}>📝 MCQ Questions</span><span style={{color:C.txD,fontSize:12,marginLeft:8}}>({mcqs.length} total, {mcqs.filter(q=>q.is_active).length} active)</span></div>
            <div style={{display:"flex",gap:6}}>
              <Btn v="secondary" onClick={exportMCQ} sz="sm">📤 Export MCQ</Btn>
              <label style={{display:"inline-flex",cursor:"pointer"}}><input type="file" accept=".json" onChange={importMCQ} style={{display:"none"}}/><span style={{padding:"7px 14px",borderRadius:9,border:`1.5px solid ${C.bd}`,background:C.s2,color:C.tx,fontSize:12,fontWeight:600,display:"inline-flex",alignItems:"center",gap:6}}>📥 Import MCQ</span></label>
            </div>
          </div>
          <p style={{color:C.txD,fontSize:11,margin:0}}>JSON format: array of objects with question, option_a, option_b, option_c, option_d, correct_option (0-3), category, explanation</p>
        </div>

        {/* Scenario Section */}
        <div style={{background:C.s1,borderRadius:9,padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div><span style={{color:"#fff",fontSize:13,fontWeight:600}}>✍️ Written Scenarios</span><span style={{color:C.txD,fontSize:12,marginLeft:8}}>({scenarios.length} total, {scenarios.filter(s=>s.is_active).length} active)</span></div>
            <div style={{display:"flex",gap:6}}>
              <Btn v="secondary" onClick={exportScenarios} sz="sm">📤 Export Scenarios</Btn>
              <label style={{display:"inline-flex",cursor:"pointer"}}><input type="file" accept=".json" onChange={importScenarios} style={{display:"none"}}/><span style={{padding:"7px 14px",borderRadius:9,border:`1.5px solid ${C.bd}`,background:C.s2,color:C.tx,fontSize:12,fontWeight:600,display:"inline-flex",alignItems:"center",gap:6}}>📥 Import Scenarios</span></label>
            </div>
          </div>
          <p style={{color:C.txD,fontSize:11,margin:0}}>JSON format: array of objects with title, scenario, category, rubric (array of strings)</p>
        </div>
      </Card>}

      {/* Settings Panel */}
      {showSettings&&<Card style={{marginBottom:16}}>
        <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:12,fontFamily:FN.d}}>⚙️ Assessment Settings</h3>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <label style={{color:C.txS,fontSize:11,fontWeight:600,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em"}}>Theme</label>
            <select value={settings.theme||"midnight"} onChange={e=>setSettings(p=>({...p,theme:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${C.bd}`,background:`${C.s1}dd`,color:C.tx,fontFamily:FN.b,fontSize:14,outline:"none"}}>
              <option value="midnight">Midnight Blue</option>
              <option value="aurora">Aurora Indigo</option>
              <option value="ember">Ember Orange</option>
              <option value="ocean">Ocean Cyan</option>
            </select>
          </div>
          <div>
            <label style={{color:C.txS,fontSize:11,fontWeight:600,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em"}}>Auth Mode</label>
            <select value={settings.auth_mode} onChange={e=>setSettings(p=>({...p,auth_mode:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${C.bd}`,background:`${C.s1}dd`,color:C.tx,fontFamily:FN.b,fontSize:14,outline:"none"}}>
              <option value="direct">Direct (no magic link)</option>
              <option value="magic_link">Magic Link (email required)</option>
            </select>
          </div>
          <Inp label="Assessment Title" value={settings.assessment_title||"AI Assessment"} onChange={e=>setSettings(p=>({...p,assessment_title:e.target.value}))}/>
          <Inp label="Passing Score (%)" type="number" value={settings.passing_score} onChange={e=>setSettings(p=>({...p,passing_score:e.target.value}))}/>
          <Inp label="Max Attempts" type="number" value={settings.max_attempts||"3"} onChange={e=>setSettings(p=>({...p,max_attempts:e.target.value}))}/>
          <Inp label="MCQ Count" type="number" value={settings.mcq_count} onChange={e=>{
            const mc=parseInt(e.target.value||0);const sc=parseInt(settings.scenario_count||0);
            setSettings(p=>({...p,mcq_count:String(mc),test_duration:String((mc*1+sc*10)*60)}));
          }}/>
          <Inp label="Scenario Count" type="number" value={settings.scenario_count} onChange={e=>{
            const sc=parseInt(e.target.value||0);const mc=parseInt(settings.mcq_count||0);
            setSettings(p=>({...p,scenario_count:String(sc),test_duration:String((mc*1+sc*10)*60)}));
          }}/>
          <Inp label="Duration (minutes)" type="number" value={String(Math.floor(parseInt(settings.test_duration||0)/60))} onChange={e=>setSettings(p=>({...p,test_duration:String(parseInt(e.target.value||0)*60)}))}/>
          <Inp label="Prompt Min Words" type="number" value={settings.prompt_min_words||"30"} onChange={e=>setSettings(p=>({...p,prompt_min_words:e.target.value}))}/>
          <Inp label="Prompt Max Words" type="number" value={settings.prompt_max_words||"300"} onChange={e=>setSettings(p=>({...p,prompt_max_words:e.target.value}))}/>
          <Inp label="Base Font Size (px)" type="number" value={settings.base_font_size||"16"} onChange={e=>setSettings(p=>({...p,base_font_size:e.target.value}))}/>
          <div>
            <label style={{color:C.txS,fontSize:11,fontWeight:600,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em"}}>AI Scoring (Gemini)</label>
            <div onClick={()=>setSettings(p=>({...p,ai_enabled:p.ai_enabled==="true"?"false":"true"}))} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:9,border:`1.5px solid ${C.bd}`,background:C.s1,cursor:"pointer"}}>
              <div style={{width:40,height:22,borderRadius:11,background:settings.ai_enabled==="true"?C.gn:C.txD,transition:"all .2s",position:"relative"}}>
                <div style={{width:18,height:18,borderRadius:9,background:"#fff",position:"absolute",top:2,left:settings.ai_enabled==="true"?20:2,transition:"all .2s"}}/>
              </div>
              <span style={{color:settings.ai_enabled==="true"?C.gn:C.txD,fontSize:13,fontWeight:600}}>{settings.ai_enabled==="true"?"ON":"OFF"}</span>
            </div>
            <span style={{color:C.txD,fontSize:10,marginTop:4,display:"block"}}>Off = no Gemini calls, faster testing</span>
          </div>
        </div>
        <div style={{background:C.s1,borderRadius:8,padding:10,marginBottom:12}}>
          <span style={{color:C.txD,fontSize:11}}>Auto-calc suggestion: {settings.mcq_count} MCQ × 1 min + {settings.scenario_count} scenarios × 10 min = <strong style={{color:C.ac}}>{parseInt(settings.mcq_count||0)*1+parseInt(settings.scenario_count||0)*10} min</strong> (you can override duration above)</span>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <Btn onClick={async()=>{
            for(const[k,v]of Object.entries(settings)){await saveSetting(k,v,admin.email);}
            await loadSettings();
            alert("Settings saved! Changes take effect for new tests.");
          }} sz="sm">Save Settings</Btn>
          <span style={{color:C.txD,fontSize:11}}>Changes apply to new tests immediately.</span>
        </div>
      </Card>}

      {/* Team AI Analysis */}
      {teamAI&&<Card style={{marginBottom:16,borderLeft:`3px solid ${C.ac}`}}>
        <h3 style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:8,fontFamily:FN.d}}>🤖 AI Team Analysis</h3>
        {teamAI.executive_summary&&<p style={{color:C.txS,fontSize:13,lineHeight:1.5,marginBottom:8}}>{teamAI.executive_summary}</p>}
        {teamAI.overall_readiness&&<div style={{marginBottom:8}}><Badge color={teamAI.overall_readiness==="Strong"?C.gn:teamAI.overall_readiness==="Good"?C.ac:C.am}>{teamAI.overall_readiness} Readiness</Badge></div>}
        {teamAI.top_3_organizational_gaps?.length>0&&<div style={{marginBottom:8}}><p style={{color:C.txD,fontSize:11,fontWeight:600,marginBottom:4}}>TOP GAPS:</p>{teamAI.top_3_organizational_gaps.map((g,i)=><p key={i} style={{color:C.txS,fontSize:12,paddingLeft:8,borderLeft:`2px solid ${C.rd}40`,marginBottom:3}}>{g}</p>)}</div>}
        {teamAI.training_recommendations?.length>0&&<div><p style={{color:C.txD,fontSize:11,fontWeight:600,marginBottom:4}}>RECOMMENDATIONS:</p>{teamAI.training_recommendations.map((r,i)=><p key={i} style={{color:C.txS,fontSize:12,paddingLeft:8,borderLeft:`2px solid ${C.gn}40`,marginBottom:3}}>{r}</p>)}</div>}
      </Card>}

      {/* Employee Table */}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><h3 style={{color:"#fff",fontSize:14,fontWeight:700,fontFamily:FN.d}}>All Employees</h3></div>
        <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead><tr>{["Name/Email","Dept","Score","MCQ","Written","Flags","Status","Date",""].map(h=><th key={h} style={{textAlign:"left",padding:"8px 10px",borderBottom:`2px solid ${C.bd}`,color:C.txD,fontWeight:600,fontSize:10,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
          <tbody>
            {scored.map(r=>{const e=r.employees||{};return<tr key={r.id} style={{cursor:"pointer"}} onClick={()=>setSel(r)}>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`}}><div style={{color:C.tx,fontWeight:500,fontSize:13}}>{e.name||"—"}</div><div style={{color:C.txD,fontFamily:FN.m,fontSize:11}}>{e.email}</div></td>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`,color:C.txS,fontSize:12}}>{e.team_functions?.name||"—"}</td>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`}}><span style={{color:r.passed?C.gn:C.rd,fontWeight:700,fontFamily:FN.m}}>{r.total_score}%</span></td>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`,color:C.txS,fontFamily:FN.m}}>{r.mcq_score}%</td>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`,color:C.txS,fontFamily:FN.m}}>{r.scenario_score!==null&&r.scenario_score!==undefined?r.scenario_score+"%":<Badge color={C.txD}>Pending</Badge>}</td>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`}}>{r.tab_switches>0?<Badge color={C.rd}>⚠{r.tab_switches}</Badge>:<span style={{color:C.txD}}>0</span>}</td>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`}}><Badge color={r.passed?C.gn:C.rd}>{r.passed?"Pass":"Fail"}</Badge></td>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`,color:C.txD,fontSize:11}}>{new Date(r.started_at).toLocaleDateString()}</td>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`}}><span style={{color:C.ac,fontSize:11,fontWeight:600}}>Details →</span></td>
            </tr>})}
            {employees.filter(e=>!results.find(r=>r.employee_id===e.id)).map(e=><tr key={e.id} style={{opacity:.45}}>
              <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`}}><div style={{color:C.txD,fontSize:12}}>{e.name||"—"}</div><div style={{fontFamily:FN.m,fontSize:11,color:C.txD}}>{e.email}</div></td>
              <td colSpan={7} style={{padding:"8px 10px",borderBottom:`1px solid ${C.bd}`,color:C.txD}}>Not started</td><td/>
            </tr>)}
          </tbody>
        </table></div>
      </Card>
    </Wrap>
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════════════════════ */
export default function App(){
  const [screen,setScreen]=useState("loading");
  const [employee,setEmployee]=useState(null);const [admin,setAdmin]=useState(null);
  const [questions,setQuestions]=useState([]);const [answerKey,setAnswerKey]=useState({});const [seed,setSeed]=useState(0);const [result,setResult]=useState(null);

  // Load settings from DB on startup
  useEffect(()=>{loadSettings().then(()=>setScreen("email"));},[]);

  const startTest=async()=>{
    const[{data:mcqs},{data:scns}]=await Promise.all([
      supabase.from("questions_mcq").select("*").eq("is_active",true),
      supabase.from("questions_scenario").select("*").eq("is_active",true),
    ]);
    const s=genSeed();setSeed(s);
    const{questions:qs,answerKey:ak}=buildTest(mcqs||[],scns||[],s);
    setQuestions(qs);setAnswerKey(ak);
    setScreen("test");
  };

  switch(screen){
    case "loading":return<div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}><style>{GCSS}</style><span style={{color:C.ac}}>Loading...</span></div>;
    case "email":return<EmailScreen onVerified={emp=>{setEmployee(emp);setScreen("instructions");}} onAdmin={()=>setScreen("admin_login")}/>;
    case "admin_login":return<AdminLogin onLogin={a=>{setAdmin(a);setScreen("admin");}} onBack={()=>setScreen("email")}/>;
    case "instructions":return<Instructions employee={employee} onStart={startTest}/>;
    case "test":return<TestScreen employee={employee} questions={questions} answerKey={answerKey} seed={seed} onComplete={r=>{setResult(r);setScreen("result");}}/>;
    case "result":return<ResultScreen result={result}/>;
    case "admin":return<AdminDash admin={admin} onLogout={()=>{setAdmin(null);setScreen("email");}}/>;
    default:return<EmailScreen onVerified={emp=>{setEmployee(emp);setScreen("instructions");}} onAdmin={()=>setScreen("admin_login")}/>;
  }
}
